/**
 * mol_info_utils.js
 * @typedef {import('BMapsModel').Atom} Atom
 */

import {
    bfdParseSpec, RESIDUE_SEQ_NUMBER_UNSPECIFIED, MODEL_NUMBER_UNSPECIFIED,
} from 'BMapsUtil/bfd_spec_parsing';

// Return the residue spec for an atom
// Atoms could be a part of a ligand or a modified compound.
// They are represented differently in BFD server, so we need to return a different spec for them.
/**
 * @param {Atom} atom
 * @param {boolean} forceSpec
 * @returns {string}
 */
export function getFullResidueId(atom, forceSpec=false) {
    // isCompound: non-ligand compounds
    const isCompound = (atom.residue && atom.residue.isCompound);
    const isFragment = atom.fragment;
    const useSpec = !isFragment && (!isCompound || forceSpec);

    if (useSpec) {
        // Protein and ligand atoms (incl cofactors, ions)
        // should return a full spec, using the pdb id (resn).
        const insCode = atom.icode;
        const altLoc = atom.altLoc;
        const modelNum = atom.modelNum;
        return formResidueSpec(atom.resn, atom.resi, atom.chain, { insCode, altLoc, modelNum });
    } else if (isFragment) {
        // Fragment specs are the fragment name and the index
        const fragName = atom.resname || atom.resn;
        return formFragmentSpec(fragName, atom.resi);
    } else {
        // A non-ligand compound spec is just the resname.
        return atom.resname;
    }
}

export function formResidueSpec(resn, resi, chain, { insCode, altLoc, modelNum }={}) {
    const hasInsCode = insCode && insCode.length > 0;
    const hasAltLoc = altLoc && altLoc.length > 0;
    let suffix = '';
    if (insCode) suffix += insCode;
    if (hasAltLoc) {
        if (!hasInsCode) suffix += '_';
        suffix += altLoc;
    }
    const resNameBase = resn;
    const model = (modelNum && modelNum !== 0) ? `#${modelNum}` : '';
    return `${resNameBase}.${resi}${suffix}:${chain}${model}`;
}

/**
 * Parse a residue spec into parts, keyed by BMaps fields.
 * @param {string} spec
 * @returns {{
 *     resn: string,
 *     resi: number,
 *     chain: string,
 *     insCode: string,
 *     altLoc: string,
 *     modelNum: number,
 * }}
 */
export function parseResidueSpec(spec) {
    const parsed = bfdParseSpec(spec);
    if (!parsed) return null;

    return {
        resn: parsed.resname,
        resi: parsed.seqnum !== RESIDUE_SEQ_NUMBER_UNSPECIFIED ? parsed.seqnum : null,
        chain: parsed.chain,
        insCode: parsed.icode || null,
        altLoc: parsed.altloc || null,
        modelNum: parsed.modelnum !== MODEL_NUMBER_UNSPECIFIED ? parsed.modelnum : null,
    };
}

/**
 * One bfd-server -nameMappings clause mapping a residue to an AMBER name (`AMBER:.1:B=LIG_2`).
 * AMBER only, since that is what sets a residue's atom-group identity; add a PDB variant if
 * a caller ever needs one.
 * @param {string} resSpec The residue as it appears in the incoming file, before any renaming.
 *   The name may be left empty (`formResidueSpec('', resi, chain)`) to match by position, as
 *   bfd-server treats a blank name as a wildcard. A name must be the file-side one, never a
 *   post-load resSpec name like 'LIG_2.1:B', or it silently fails to match.
 * @param {string} name
 * @returns {string}
 */
export function buildAmberNameMapping(resSpec, name) {
    return `AMBER:${resSpec}=${name}`;
}

/**
 * Return if a spec is "complete" with name, sequence number, and chain.
 * @param {string} spec
 * @returns {boolean}
 */
export function isFullResidueSpec(spec) {
    const parsed = bfdParseSpec(spec);
    if (!parsed) return false;
    return !!(parsed.resname && parsed.seqnum !== RESIDUE_SEQ_NUMBER_UNSPECIFIED && parsed.chain);
}

export function formFragmentSpec(fragName, fragIndex) {
    const index = (fragIndex != null) ? `.${fragIndex}` : '';
    return `${fragName}${index}`;
}

export function joinAtomSpecs(atoms, join, forceSpec) {
    return atoms
        .map((atom) => getFullResidueId(atom, forceSpec))
        .join(join);
}

/**
 * @description Collect atoms into alt-loc groups for each alt-loc site.
 * altLocKey is the resSpec without the altLoc, shared by the alternatives at a particular site.
 * This function maps from altLocKey to the collection atoms of for each alternative (also a map):
 * generalAltLocKey -> (specificResSpec -> specificAtoms)
 *
 * @param {Atom[]} atoms
 * @returns {{ [altLocKey: string]: {[resSpec: string]: Atom[]} }}
 */
export function getAltLocMap(atoms) {
    /** @type {{ [altLocKey: string]: {[resSpec: string]: Atom[]} }} */
    const results = {};
    for (const atom of atoms) {
        if (!atom.altLoc) continue;
        const resSpec = getFullResidueId(atom); // full spec, including altLoc
        const altLocKey = getAltLocKey(atom); // resSpec without altLoc
        /** @type {{ [resSpec: string]: Atom[] }} */
        let keyMap = results[altLocKey];
        if (!keyMap) {
            keyMap = {};
            results[altLocKey] = keyMap;
        }
        /** @type {Atom[]} */
        let myGroup = keyMap[resSpec];
        if (!myGroup) {
            myGroup = [];
            keyMap[resSpec] = myGroup;
        }
        myGroup.push(atom);
    }
    return results;
}

/**
 * Return a full spec without the altLoc,
 * to be used as a unifying key for other alternatives for the same residue.
 * @param {Atom} atom
 * @returns {string}
 */
export function getAltLocKey(atom) {
    return formResidueSpec(atom.resn, atom.resi, atom.chain,
        { insCode: atom.icode, modelNum: atom.modelNum });
}

/**
 * @description Scan list of atoms and produce a set of allowed altLocs residue specs.
 * This will allow the first altLoc found for each residue with altLocs.
 * @param {Atom[]} atoms
 * @returns {Set<string>} includedAltLocSpecs
 */
export function getIncludedAltLocSpecs(atoms) {
    const altLocMap = getAltLocMap(atoms);
    if (Object.keys(altLocMap).length > 0) console.log('getIncludedAltLocSpecs altLocs:', altLocMap);
    const includedAltLocSpecs = new Set();
    // For each residue that has alt locs, use the atoms of the first we find
    for (const [altLocKey, specToAtomsMap] of Object.entries(altLocMap)) {
        const resSpec = Object.keys(specToAtomsMap)[0];
        includedAltLocSpecs.add(resSpec);
        console.log(`For alt loc ${altLocKey}, using ${resSpec}`);
    }
    return includedAltLocSpecs;
}

/**
 * Filter atoms based on altLoc. If includedSpecs is provided, only include those residues,
 * otherwise scan the atoms to identify the residues to include.
 * @param {Atom[]} atoms
 * @param {Set<string>?} includedSpecs
 * @returns {Atom[]}
 */
export function filterAltLocs(atoms, includedSpecs) {
    const includedAltLocSpecs = includedSpecs || getIncludedAltLocSpecs(atoms);
    return atoms.filter((atom) => !atom.altLoc || includedAltLocSpecs.has(getFullResidueId(atom)));
}

export function validateCompoundName(name) {
    // Originally, rename validation enforced 128 max, but not compound import.
    // Removing the enforcement in the principle of limiting restrictions and consistency.
    // if (name.length > 128) {
    //     return 'Max 128 characters!';
    // }
    if (/[\t\n\r\f\v]+/.test(name)) {
        return 'Compound names cannot contain tabs or whitespace other than spaces';
    }
    return undefined;
}
